// ============================================================
// THE DISPOSITION LADDER - how somebody stands towards the party, and what
// a social test PROPOSES for it.
//
// `DISPOSITIONS` (cast.ts) runs from Hostile to Devoted. When someone is
// tested AGAINST a member of the cast on a social skill, the result
// suggests a step on that ladder; the GM accepts it or leaves them as they
// are. Nothing here changes anybody.
//
//   - Winning them over (Charm, Command, Inquiry, Deceive): a pass warms
//     them a step; a failure by three degrees or more cools them a step.
//   - Leaning on them (Intimidate, Interrogation): a pass gets what was
//     asked and wins no regard; any failure cools them a step.
//
// Somebody with no disposition set is read as Neutral.
//
// Pure. Zero React dependency.
// ============================================================
import type { TestSubject } from '../opposedTests';
import { DISPOSITIONS } from './cast';

export type Disposition = (typeof DISPOSITIONS)[number];

const NEUTRAL: Disposition = 'Neutral';

type Manner = 'winsOver' | 'leansOn';

/** The skills that move a disposition, and how. Anything else proposes nothing. */
const MANNER: Record<string, Manner> = {
  charm: 'winsOver',
  command: 'winsOver',
  inquiry: 'winsOver',
  deceive: 'winsOver',
  intimidate: 'leansOn',
  interrogation: 'leansOn',
};

/** Failing to win someone over by this many degrees cools them. */
const COOLS_FROM = 3;

const isDisposition = (value: unknown): value is Disposition => DISPOSITIONS.includes(value as Disposition);

/** The disposition `by` steps warmer (or colder), never past either end. An unset one starts at Neutral. */
export function stepDisposition(current: string | undefined, by: number): Disposition {
  const from = DISPOSITIONS.indexOf(isDisposition(current) ? current : NEUTRAL);
  return DISPOSITIONS[Math.max(0, Math.min(DISPOSITIONS.length - 1, from + Math.round(by)))];
}

export interface DispositionProposal {
  /** Where they would stand. Equal to where they stand now when nothing is proposed to change. */
  disposition: Disposition;
  reason: string;
}

/** The step this manner of test takes for this result. */
function stepFor(manner: Manner, passed: boolean, margin: number): number {
  if (manner === 'winsOver') {
    if (passed) return 1;
    return margin >= COOLS_FROM ? -1 : 0;
  }
  return passed ? 0 : -1;
}

const REASONS: Record<Manner, { pass: string; fail: string; badFail: string }> = {
  winsOver: { pass: 'They are won over.', fail: 'It does not land, and does no harm.', badFail: 'It goes badly.' },
  leansOn: {
    pass: 'They give way, and think no better of you for it.',
    fail: 'They do not give way, and resent the attempt.',
    badFail: 'They do not give way, and resent the attempt.',
  },
};

/**
 * What a social test against somebody proposes for how they stand. Null
 * for a subject that is not social. Total over every outcome otherwise.
 */
export function proposeDisposition(
  subject: TestSubject,
  outcome: { passed: boolean; margin: number },
  current: string | undefined,
): DispositionProposal | null {
  const manner = subject.kind === 'skill' ? MANNER[subject.id] : undefined;
  if (!manner) return null;
  const from = stepDisposition(current, 0);
  const to = stepDisposition(current, stepFor(manner, outcome.passed, outcome.margin));
  const said = REASONS[manner];
  const why = outcome.passed ? said.pass : outcome.margin >= COOLS_FROM ? said.badFail : said.fail;
  return { disposition: to, reason: to === from ? `${why} They stay ${from}.` : `${why} Proposed: ${to}.` };
}
